import { useStore } from '@tanstack/react-form';
import { CircleCheckBig } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';

import Transition from '@/components/Common/Transition';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/ui/select';
import { realtimeFormOptions } from '@/entities/(notification)/realtime';
import useStructuredFilterQuery from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import StructuredFilter from '@/features/StructuredFilter/StructuredFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import { withForm } from '@/shared/ui/form';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import {
  realtimeBasisOptions,
  WORKFLOW_REALTIME_BASIS,
  WorkspaceRealtimeOptionDic,
} from '@/types/workflowConst';

const basisOptions = realtimeBasisOptions.map((key) => ({
  value: key,
  label: WorkspaceRealtimeOptionDic[key],
}));

export const RealtimeTriggerSection = withForm({
  ...realtimeFormOptions,
  render: function RealtimeTriggerSection({ form }) {
    const { parseStructuredFilter } = useStructuredFilter();
    const initializedRef = useRef(false);
    const filterset = useStore(form.store, (state) => state.values.filterset);

    // 필터 변경 콜백
    const onFilterChangeCallback = useCallback(
      (filterSetQuery: StructuredFilterQueryFormatT[]) => {
        const structuredFilter =
          structuredFilterUtils.buildStructuredFilter(filterSetQuery);
        // buildStructuredFilter는 StructuredFilter | undefined 반환
        // form의 filterset은 Record<string, unknown>으로 타입됨
        form.setFieldValue(
          'filterset',
          (structuredFilter ?? {}) as Record<string, unknown>,
        );
      },
      [form],
    );

    // useStructuredFilterQuery 훅
    const {
      filterQueryFormats: currFilterSetQuery,
      onFilterSetChange,
      clearFilter,
    } = useStructuredFilterQuery({
      onFilterChangeCallback,
    });

    // 초기 filterset 값으로 필터 쿼리 초기화
    useEffect(() => {
      if (!initializedRef.current && filterset) {
        // Record<string, unknown>을 StructuredFilter로 변환
        const filterSetQuery = parseStructuredFilter(
          filterset as StructuredFilter,
        );
        onFilterSetChange(filterSetQuery);
        initializedRef.current = true;
      }
    }, [filterset, parseStructuredFilter, onFilterSetChange]);

    return (
      <div className="flex flex-col gap-5 rounded-lg border p-5">
        {/* 헤더 */}
        <div className="flex items-center gap-2">
          <CircleCheckBig className="size-5 text-green-600" />
          <span className="text-sm font-semibold">Trigger</span>
        </div>

        {/* Basis 선택 */}
        <div className="grid grid-cols-[120px_1fr] items-center gap-3">
          <span className="text-sm text-muted-foreground">Notify on</span>
          <form.AppField
            name="basis"
            children={(field) => (
              <Select
                value={String(field.state.value)}
                onValueChange={(value) =>
                  field.handleChange(Number(value) as WORKFLOW_REALTIME_BASIS)
                }
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {basisOptions.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {/* Filter (SELECTED_FILTER인 경우에만) */}
        <form.Subscribe selector={(state) => state.values.basis}>
          {(basis) => (
            <Transition
              status={basis === WORKFLOW_REALTIME_BASIS.SELECTED_FILTER}
            >
              <div className="grid grid-cols-[120px_1fr] items-start gap-3">
                <span className="pt-2 text-sm text-muted-foreground">
                  Filter
                </span>
                <StructuredFilter
                  disAllowFilter={[
                    FEEDBACK_COLUMNS.STATUS,
                    FEEDBACK_COLUMNS.TICKET_STATUS,
                  ]}
                  values={currFilterSetQuery}
                  onChange={onFilterSetChange}
                  clearFilter={clearFilter}
                />
              </div>
            </Transition>
          )}
        </form.Subscribe>
      </div>
    );
  },
});
